/**
 * ParticipantHeader - Compact header for Participant Workspace
 *
 * Shows plan title, progress bar, and back button.
 * NO tabs - this is a single-screen focused experience.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme/colors';
import { getPlanStatusColor } from '../../utils/planHelpers';
import { PlanStatus } from '../../types/generated/graphql';

interface StatusCounts {
  todo: number;
  inProgress: number;
  blocked: number;
  completed: number;
}

interface ParticipantHeaderProps {
  planId: string;
  planTitle: string;
  planStatus: string;
  updatedAt: string;
  totals: StatusCounts;
  visibleSteps: number;
  totalSteps: number;
}

export const ParticipantHeader: React.FC<ParticipantHeaderProps> = ({
  planTitle,
  planStatus,
  updatedAt,
  totals,
  visibleSteps,
  totalSteps,
}) => {
  const navigate = useNavigate();

  // Calculate progress
  const myTotal = totals.todo + totals.inProgress + totals.blocked + totals.completed;
  const myDone = totals.completed;
  const percentComplete = myTotal > 0 ? (myDone / myTotal) * 100 : 0;

  return (
    <Box
      sx={{
        bgcolor: colors.card,
        borderBottom: `1px solid ${colors.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Row 1: Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 1.5,
          minHeight: 56,
        }}
      >
        {/* Back link */}
        <Tooltip title="Back to Plans">
          <IconButton
            onClick={() => navigate('/app/plans')}
            size="small"
            sx={{
              color: colors.textSecondary,
              '&:hover': {
                bgcolor: colors.gray[100],
                color: colors.blue[600],
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Box sx={{ width: '1px', height: 24, bgcolor: colors.gray[300], flexShrink: 0 }} />

        {/* Plan title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: colors.textPrimary,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {planTitle || 'Untitled Plan'}
          </Typography>
        </Box>

        {/* Participant indicator */}
        <Chip
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          label="My Tasks"
          size="small"
          sx={{
            bgcolor: colors.blue[50],
            color: colors.blue[600],
            border: `1px solid ${colors.blue[200]}`,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            flexShrink: 0,
            '& .MuiChip-icon': {
              color: colors.blue[600],
            },
          }}
        />

        {/* Status chip */}
        <Chip
          label={planStatus}
          size="small"
          sx={{
            bgcolor: `${getPlanStatusColor(planStatus as PlanStatus)}15`,
            color: getPlanStatusColor(planStatus as PlanStatus),
            border: `1px solid ${getPlanStatusColor(planStatus as PlanStatus)}30`,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Row 2: Metadata row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 3,
          pb: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          Updated {new Date(updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>
          &bull;
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          {myDone}/{myTotal} tasks complete
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>
          &bull;
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>
          ({visibleSteps} of {totalSteps} plan tasks assigned to you)
        </Typography>
      </Box>

      {/* Thin progress line */}
      <LinearProgress
        variant="determinate"
        value={percentComplete}
        sx={{
          height: 3,
          bgcolor: colors.gray[100],
          '& .MuiLinearProgress-bar': {
            bgcolor: colors.green[600],
          },
        }}
      />
    </Box>
  );
};

export default ParticipantHeader;
